import React, { useEffect, useMemo, useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Button,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import {
  marketplaceCartService,
  CART_EVENT_NAME,
  MarketplaceCart,
} from '../../services/marketplaceCartService';
import { useNavigate } from 'react-router-dom';

interface MarketplaceCartDrawerProps {
  open: boolean;
  onClose: () => void;
}

const formatPrice = (amount: number) => `$${(amount / 100).toFixed(2)}`;

const MarketplaceCartDrawer: React.FC<MarketplaceCartDrawerProps> = ({
  open,
  onClose,
}) => {
  const navigate = useNavigate();
  const [cart, setCart] = useState<MarketplaceCart>(
    marketplaceCartService.getCart(),
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    setCart(marketplaceCartService.getCart());
  }, [open]);

  useEffect(() => {
    const handleCartUpdate = () => {
      setCart(marketplaceCartService.getCart());
    };

    window.addEventListener(CART_EVENT_NAME, handleCartUpdate);
    window.addEventListener('storage', handleCartUpdate);

    return () => {
      window.removeEventListener(CART_EVENT_NAME, handleCartUpdate);
      window.removeEventListener('storage', handleCartUpdate);
    };
  }, []);

  const { totalItems, subtotal } = useMemo(() => {
    let total = 0;
    let totalItems = 0;

    cart.items.forEach(item => {
      const price = item.variant?.price || 0;
      total += price * item.quantity;
      totalItems += item.quantity;
    });

    return { subtotal: total, totalItems };
  }, [cart.items]);

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 360, maxWidth: '90vw', p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Shopping Cart
          </Typography>
          <IconButton onClick={onClose} aria-label="close cart">
            <Close />
          </IconButton>
        </Box>
        <Typography variant="body2" color="text.secondary">
          {totalItems} item{totalItems !== 1 ? 's' : ''}
        </Typography>

        <Divider sx={{ my: 2 }} />

        {cart.items.length === 0 ? (
          <Typography variant="body1" color="text.secondary">
            Your cart is empty.
          </Typography>
        ) : (
          <List disablePadding>
            {cart.items.map(item => (
              <ListItem key={`${item.productId}-${item.variantId}`} divider>
                <ListItemAvatar>
                  <Avatar
                    variant="rounded"
                    src={item.product?.images?.[0]}
                    alt={item.product?.title || 'Product'}
                  />
                </ListItemAvatar>
                <ListItemText
                  primary={item.product?.title || 'Marketplace Item'}
                  secondary={`Qty ${item.quantity} • ${formatPrice(
                    (item.variant?.price || 0) * item.quantity,
                  )}`}
                />
              </ListItem>
            ))}
          </List>
        )}

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="subtitle2">Subtotal</Typography>
          <Typography variant="subtitle2">{formatPrice(subtotal)}</Typography>
        </Box>

        <Button
          variant="contained"
          fullWidth
          onClick={() => {
            onClose();
            navigate('/marketplace/cart');
          }}
          disabled={cart.items.length === 0}
          sx={{ mb: 1 }}
        >
          View Cart
        </Button>
        <Button
          variant="outlined"
          fullWidth
          onClick={() => {
            onClose();
            navigate('/marketplace/checkout');
          }}
          disabled={cart.items.length === 0}
        >
          Checkout
        </Button>
      </Box>
    </Drawer>
  );
};

export default MarketplaceCartDrawer;
